import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnDestroy,
  Input,
  computed,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export interface NumericAnswerValue {
  /** Valeur attendue. */
  value: number | null;
  /** Tolérance ± acceptée autour de la valeur attendue (0 = valeur exacte). */
  tolerance: number;
}

export type NumericAnswerForm = FormGroup<{
  value: FormControl<number | null>;
  tolerance: FormControl<number | null>;
}>;

/** Intervalle accepté, calculé à partir de la valeur et de la tolérance. */
export interface NumericRange {
  min: number;
  max: number;
}

/**
 * Éditeur de réponse numérique (`numeric`) : une valeur attendue et une
 * tolérance ±. Affiche en direct l'intervalle qui sera accepté.
 */
@Component({
  selector: 'app-numeric-answer',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './numeric-answer.component.html',
  styleUrl: './numeric-answer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumericAnswerComponent implements OnChanges, OnDestroy {
  /** Sous-formulaire de la réponse numérique, construit par le parent. */
  @Input({ required: true }) group!: NumericAnswerForm;

  /** Compteur de révision : incrémenté à chaque changement du sous-formulaire. */
  private readonly revision = signal(0);
  private subscription?: Subscription;

  /** Intervalle accepté [valeur - tolérance ; valeur + tolérance]. */
  readonly range = computed<NumericRange | null>(() => {
    this.revision();
    const raw = this.group?.getRawValue();
    if (!raw || raw.value === null || !Number.isFinite(raw.value)) {
      return null;
    }
    const tolerance =
      raw.tolerance !== null && Number.isFinite(raw.tolerance)
        ? Math.abs(raw.tolerance)
        : 0;
    return { min: raw.value - tolerance, max: raw.value + tolerance };
  });

  /** Vrai si la tolérance est nulle : seule la valeur exacte sera acceptée. */
  readonly isExact = computed<boolean>(() => {
    const range = this.range();
    return range !== null && range.min === range.max;
  });

  ngOnChanges(): void {
    this.subscription?.unsubscribe();
    this.revision.update((value) => value + 1);
    this.subscription = this.group?.valueChanges.subscribe(() =>
      this.revision.update((value) => value + 1),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
